import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../hooks/useAuth';
import AddressAutocomplete from '../components/AddressAutocomplete';
import LiabilityAgreement from '../components/Liabilityagreement';
import { useLang } from '../hooks/useLang';
import './PostTrip.css';

const GERMAN_CITIES  = ['Berlin','Hamburg','Munich','Frankfurt','Cologne','Stuttgart','Dusseldorf','Dortmund','Essen','Leipzig','Bremen','Dresden','Hanover','Nuremberg'];
const TUNISIAN_CITIES = ['Tunis','Sfax','Sousse','Monastir','Bizerte','Gabes','Ariana','Gafsa','Kairouan','Nabeul'];
const PORT_CITIES     = ['Genova','Marseille','Barcelona','Valencia','Civitavecchia'];

const PAYMENT_MODE_OPTIONS = [
  { value: 'platform', label: '💳 Full platform payment', desc: 'Sender pays the full amount via Stripe. You receive earnings in your wallet.' },
  { value: 'cash',     label: '💵 Cash at pickup',        desc: 'Sender pays only the platform fee online. You collect your fee in cash at pickup.' },
  { value: 'either',   label: '🔀 Let the sender choose', desc: 'Sender can choose online or cash. More flexibility for both sides.' },
];

const EMPTY_STOP = { stop_type: 'pickup', country: '', city: '', address: '', stop_date: '', stop_date_until: '', time_from: '', time_until: '', available_kg: '', notes: '' };

function StopCard({ stop, idx, total, isDeToTn, updateStop, moveStop, removeStop, t, isDragOver, onDragStart, onDragOver, onDrop, onDragEnd }) {
  const isPickup   = stop.stop_type === 'pickup'   || stop.stop_type === 'both';
  const isDelivery = stop.stop_type === 'delivery' || stop.stop_type === 'both';
  const countryOptions = isPickup
    ? (isDeToTn ? GERMAN_CITIES : TUNISIAN_CITIES)
    : (isDeToTn ? TUNISIAN_CITIES : GERMAN_CITIES);

  const typeColor = 'rgba(28,26,23,0.85)';

  const accentColor = stop.stop_type === 'pickup'   ? 'var(--red)'
                   : stop.stop_type === 'delivery' ? '#4CAF7D'
                   : stop.stop_type === 'port'     ? '#6496ff'
                   : 'var(--accent)';

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      style={{
        background: 'var(--card)',
        border: `1px solid ${isDragOver ? accentColor : 'var(--border)'}`,
        borderLeft: `3px solid ${accentColor}`,
        borderRadius: '14px',
        padding: '16px',
        marginBottom: '10px',
        backdropFilter: 'blur(20px)',
        opacity: isDragOver ? 0.6 : 1,
        transform: isDragOver ? 'scale(0.99)' : 'scale(1)',
        transition: 'opacity 0.15s, transform 0.15s, border-color 0.15s',
        cursor: 'grab',
      }}
    >

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(255,255,255,0.08)', borderRadius: '100px', padding: '3px 10px', fontSize: '12px', fontWeight: '700', cursor: 'grab', userSelect: 'none' }}>
          <span style={{ opacity: 0.5, letterSpacing: '1px', fontSize: '10px' }}>⠿</span>
          {t('postTrip.stopLabel').replace('{{n}}', idx + 1)}
        </div>
        <select
          value={stop.stop_type}
          onChange={e => updateStop(idx, 'stop_type', e.target.value)}
          style={{ fontSize: '12px', padding: '4px 8px', borderRadius: '8px', flex: '0 0 auto' }}
        >
          <option value="pickup">{t('postTrip.stopTypePickup')}</option>
          <option value="delivery">{t('postTrip.stopTypeDelivery')}</option>
          <option value="port">{t('postTrip.stopTypePort')}</option>
          <option value="both">{t('postTrip.stopTypeBoth')}</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px', alignItems: 'center' }}>
          {total > 2 && (
            <button type="button" onClick={() => removeStop(idx)} style={{ background: 'rgba(232,52,26,0.12)', border: '1px solid rgba(232,52,26,0.25)', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', fontSize: '14px', color: 'var(--red)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
          )}
        </div>
      </div>

      {/* City */}
      <div style={{ marginBottom: '10px' }}>
        {stop.stop_type === 'port' ? (
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '11px' }}>{t('postTrip.stopPortCity')}</label>
            <select value={stop.city} onChange={e => updateStop(idx, 'city', e.target.value)}>
              {PORT_CITIES.map(pc => <option key={pc} value={pc}>{pc}</option>)}
            </select>
          </div>
        ) : (
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '11px' }}>{t('postTrip.stopCity')}</label>
            <select value={stop.city} onChange={e => {
              updateStop(idx, 'city', e.target.value);
              updateStop(idx, 'country', isPickup && !isDelivery
                ? (isDeToTn ? 'Germany' : 'Tunisia')
                : (isDeToTn ? 'Tunisia' : 'Germany'));
            }}>
              <option value="">Select city</option>
              {countryOptions.map(co => <option key={co} value={co}>{co}</option>)}
            </select>
          </div>
        )}
      </div>

      {/* Date(s) */}
      {(stop.stop_type === 'pickup' || stop.stop_type === 'both') ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '11px' }}>{t('postTrip.stopDateFrom')}</label>
            <input type="date" value={stop.stop_date} onChange={e => updateStop(idx, 'stop_date', e.target.value)} required />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label style={{ fontSize: '11px' }}>{t('postTrip.stopDateUntil')}</label>
            <input type="date" value={stop.stop_date_until} min={stop.stop_date || undefined} onChange={e => updateStop(idx, 'stop_date_until', e.target.value)} />
          </div>
        </div>
      ) : (
        <div className="form-group" style={{ marginBottom: '10px' }}>
          <label style={{ fontSize: '11px' }}>{t('postTrip.stopDate')}</label>
          <input type="date" value={stop.stop_date} onChange={e => updateStop(idx, 'stop_date', e.target.value)} required />
        </div>
      )}

      {/* Time window */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '10px' }}>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: '11px' }}>{t('postTrip.stopTimeFrom')}</label>
          <input type="time" value={stop.time_from} onChange={e => updateStop(idx, 'time_from', e.target.value)} />
        </div>
        <div className="form-group" style={{ margin: 0 }}>
          <label style={{ fontSize: '11px' }}>{t('postTrip.stopTimeUntil')}</label>
          <input type="time" value={stop.time_until} onChange={e => updateStop(idx, 'time_until', e.target.value)} />
        </div>
      </div>

      {/* Meeting point + capacity */}
      {stop.stop_type !== 'port' && (
        <>
          <div className="form-group" style={{ margin: '0 0 10px' }}>
            <label style={{ fontSize: '11px' }}>{t('postTrip.stopAddress')}</label>
            <input
              type="text"
              placeholder={stop.stop_type === 'pickup' ? t('postTrip.stopAddressPickupHint') : t('postTrip.stopAddressDeliveryHint')}
              value={stop.address}
              onChange={e => updateStop(idx, 'address', e.target.value)}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ fontSize: '11px' }}>{t('postTrip.stopCapacity')}</label>
              <input type="number" min="0" step="1" placeholder={t('postTrip.stopCapacityHint')} value={stop.available_kg} onChange={e => updateStop(idx, 'available_kg', e.target.value)} />
            </div>
            <div className="form-group" style={{ margin: 0 }}>
              <label style={{ fontSize: '11px' }}>{t('postTrip.stopNotes')}</label>
              <input type="text" placeholder={t('postTrip.stopNotesHint')} value={stop.notes} onChange={e => updateStop(idx, 'notes', e.target.value)} />
            </div>
          </div>
        </>
      )}
    </div>
  );
}


export default function PostTrip() {
  const { user } = useAuth();
  const { t } = useLang();
  const navigate = useNavigate();
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [direction, setDirection] = useState('de_to_tn');
  const [tripMode, setTripMode] = useState('simple'); // 'simple' | 'multi'
  const [liabilityAgreed, setLiabilityAgreed] = useState(false);

  // Simple trip form
  const [form, setForm] = useState({
    origin_city: '', destination_city: '', departure_date: '', arrival_date: '',
    total_kg: '', price_per_kg: '', min_kg: '1', home_pickup: false, pickup_radius_km: 10,
    accepts_fragile: false, accepts_bulky: false, notes: '',
    offers_pickup_location: true, pickup_location_address: '',
    offers_home_delivery: false, home_delivery_fee: '',
    payment_mode: 'platform',
    accepts_documents: false,
    document_price: '',
  });

  // Multi-stop form
  const [multiForm, setMultiForm] = useState({
    total_kg: '', price_per_kg: '', min_kg: '1',
    accepts_fragile: false, accepts_bulky: false,
    offers_home_delivery: false, home_delivery_fee: '',
    payment_mode: 'platform', notes: '',
    port_city: 'Genova',
    accepts_documents: false,
    document_price: '',  
  });
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragSrcIdx = useRef(null);
  const [stops, setStops] = useState([
    { ...EMPTY_STOP, stop_type: 'pickup', country: 'Germany', city: 'Berlin', sequence: 1 },
    { ...EMPTY_STOP, stop_type: 'delivery', country: 'Tunisia', city: 'Tunis', sequence: 2 },
  ]);

  const [searchParams] = useSearchParams();
  const fromRequestId  = searchParams.get('from_request');
  const [markFullAfter, setMarkFullAfter] = useState(false);

  const isDeToTn      = direction === 'de_to_tn';
  const originCities  = isDeToTn ? GERMAN_CITIES  : TUNISIAN_CITIES;
  const destCities    = isDeToTn ? TUNISIAN_CITIES : GERMAN_CITIES;
  const originCountry = isDeToTn ? 'Germany'  : 'Tunisia';
  const destCountry   = isDeToTn ? 'Tunisia'  : 'Germany';

  useEffect(() => {
    const originCity    = searchParams.get('origin_city');
    const destCity      = searchParams.get('destination_city');
    const originCountry = searchParams.get('origin_country');
    const neededBy      = searchParams.get('needed_by');
    const kg            = searchParams.get('kg');
    const requiresDocs = searchParams.get('requires_documents') === '1';
    if (originCountry === 'Tunisia') setDirection('tn_to_de');
    if (originCity) setForm(f => ({ ...f, origin_city: originCity }));
    if (destCity)   setForm(f => ({ ...f, destination_city: destCity }));
    if (neededBy)   setForm(f => ({ ...f, arrival_date: neededBy }));
    if (kg)         setForm(f => ({ ...f, total_kg: kg, min_kg: kg }));
    if (requiresDocs) {
      setForm(f => ({ ...f, accepts_documents: true }));
      setMultiForm(f => ({ ...f, accepts_documents: true }));
    }
  }, []);

  function update(field, value) { setForm(f => ({ ...f, [field]: value })); }
  function updateMulti(field, value) { setMultiForm(f => ({ ...f, [field]: value })); }

  function switchDirection(newDir) {
    setDirection(newDir);
    setForm(f => ({ ...f, origin_city: '', destination_city: '' }));
  }

  // ── Stop management ────────────────────────────────────────────
  function addStop(type) {
    const country = type === 'pickup'
      ? (isDeToTn ? 'Germany' : 'Tunisia')
      : (isDeToTn ? 'Tunisia' : 'Germany');
    setStops(s => [...s, { ...EMPTY_STOP, stop_type: type, country, sequence: s.length + 1 }]);
  }

  function removeStop(idx) {
    setStops(s => s.filter((_, i) => i !== idx).map((st, i) => ({ ...st, sequence: i + 1 })));
  }

  function updateStop(idx, field, value) {
    setStops(s => s.map((st, i) => i === idx ? { ...st, [field]: value } : st));
  }

  function moveStop(idx, dir) {
    setStops(s => {
      const arr = [...s];
      const swap = idx + dir;
      if (swap < 0 || swap >= arr.length) return arr;
      [arr[idx], arr[swap]] = [arr[swap], arr[idx]];
      return arr.map((st, i) => ({ ...st, sequence: i + 1 }));
    });
  }

  const pickupStops   = stops.filter(s => s.stop_type === 'pickup'   || s.stop_type === 'both');
  const deliveryStops = stops.filter(s => s.stop_type === 'delivery' || s.stop_type === 'both');

  // ── Submit simple trip ─────────────────────────────────────────
  async function handleSubmitSimple(e) {
    e.preventDefault();
    setError('');
    if (!liabilityAgreed) { setError('Please agree to the Liability Disclaimer.'); return; }
    if (!form.offers_pickup_location && !form.offers_home_delivery) { setError('Please offer at least one drop-off option.'); return; }
    if (form.offers_pickup_location && !form.pickup_location_address.trim()) { setError('Please enter the pickup location address.'); return; }
    if (form.arrival_date && form.departure_date && form.arrival_date < form.departure_date) { setError('Arrival date cannot be before departure date.'); return; }

    setLoading(true);
    const { data: tripData, error: tripErr } = await supabase.from('trips').insert({
      ...form,
      transporter_id: user.id,
      origin_country: originCountry,
      destination_country: destCountry,
      available_kg: parseFloat(form.total_kg),
      total_kg: parseFloat(form.total_kg),
      price_per_kg: parseFloat(form.price_per_kg),
      min_kg: parseFloat(form.min_kg) || 1,
      pickup_radius_km: form.home_pickup ? parseInt(form.pickup_radius_km) : 0,
      home_delivery_fee: form.offers_home_delivery ? parseFloat(form.home_delivery_fee) || 0 : 0,
      pickup_location_address: form.offers_pickup_location ? form.pickup_location_address : null,
      payment_mode: form.payment_mode,
      accepts_documents: form.accepts_documents,
      document_price: form.accepts_documents && form.document_price ? parseFloat(form.document_price) : null,
      is_multi_stop: false,
    }).select('id, price_per_kg, available_kg, document_price, accepts_documents').single();

    if (tripErr) { setError(tripErr.message); setLoading(false); return; }
    // Link liability agreement to the newly created trip
    await supabase.from('liability_agreements')
      .update({ trip_id: tripData.id })
      .eq('user_id', user.id)
      .is('trip_id', null)
      .eq('role', 'transporter')
      .order('agreed_at', { ascending: false })
      .limit(1);
    await handleRequestBooking(tripData);
  }

  // ── Submit multi-stop trip ─────────────────────────────────────
  async function handleSubmitMulti(e) {
    e.preventDefault();
    setError('');
    if (!liabilityAgreed) { setError('Please agree to the Liability Disclaimer.'); return; }
    if (stops.length < 2) { setError(t('postTrip.errorMinStops')); return; }
    if (pickupStops.length === 0) { setError(t('postTrip.errorNoPickup')); return; }
    if (deliveryStops.length === 0) { setError(t('postTrip.errorNoDelivery')); return; }

    for (const [i, stop] of stops.entries()) {
      if (!stop.city) { setError(`Stop ${i + 1}: city is required.`); return; }
      if (!stop.stop_date) { setError(`Stop ${i + 1} (${stop.city}): date is required.`); return; }
    }

    setLoading(true);

    // Use first pickup stop as origin, last delivery stop as destination
    const firstPickup   = pickupStops[0];
    const lastDelivery  = deliveryStops[deliveryStops.length - 1];
    const totalKg       = parseFloat(multiForm.total_kg);

    const { data: tripData, error: tripErr } = await supabase.from('trips').insert({
      transporter_id:     user.id,
      origin_city:        firstPickup.city,
      origin_country:     firstPickup.country,
      destination_city:   lastDelivery.city,
      destination_country: lastDelivery.country,
      departure_date:     firstPickup.stop_date,
      arrival_date:       lastDelivery.stop_date,
      total_kg:           totalKg,
      available_kg:       totalKg,
      price_per_kg:       parseFloat(multiForm.price_per_kg),
      min_kg:             parseFloat(multiForm.min_kg) || 1,
      accepts_fragile:    multiForm.accepts_fragile,
      accepts_bulky:      multiForm.accepts_bulky,
      offers_home_delivery: multiForm.offers_home_delivery,
      home_delivery_fee:  multiForm.offers_home_delivery ? parseFloat(multiForm.home_delivery_fee) || 0 : 0,
      offers_pickup_location: true,
      pickup_location_address: firstPickup.address || firstPickup.city,
      payment_mode:       multiForm.payment_mode,
      accepts_documents:  multiForm.accepts_documents,
      document_price:     multiForm.accepts_documents && multiForm.document_price ? parseFloat(multiForm.document_price) : null,
      notes:              multiForm.notes,
      is_multi_stop:      true,
      port_city:          multiForm.port_city,
    }).select('id, price_per_kg, available_kg, document_price, accepts_documents').single();

    if (tripErr) { setError(tripErr.message); setLoading(false); return; }
    // Link liability agreement to the newly created trip
    await supabase.from('liability_agreements')
      .update({ trip_id: tripData.id })
      .eq('user_id', user.id)
      .is('trip_id', null)
      .eq('role', 'transporter')
      .order('agreed_at', { ascending: false })
      .limit(1);

    // Insert all stops
    const stopsToInsert = stops.map((s, i) => ({
      trip_id:      tripData.id,
      sequence:     i + 1,
      stop_type:    s.stop_type,
      country:      s.country,
      city:         s.city,
      address:      s.address || null,
      stop_date:       s.stop_date,
      stop_date_until: s.stop_date_until || null,
      time_from:       s.time_from || null,
      time_until:   s.time_until || null,
      available_kg: (s.available_kg !== '' && s.available_kg !== null && parseFloat(s.available_kg) > 0) ? parseFloat(s.available_kg) : null,
      notes:        s.notes || null,
    }));

    const { error: stopsErr } = await supabase.from('trip_stops').insert(stopsToInsert);
    if (stopsErr) { setError('Trip created but stops failed: ' + stopsErr.message); setLoading(false); return; }

    await handleRequestBooking(tripData);
  }

  async function handleRequestBooking(newTrip) {
    if (fromRequestId) {
      const { data: request } = await supabase
        .from('shipment_requests')
        .select('sender_id, kg_needed, description, recipient_name, recipient_phone, needs_documents, document_type, document_count')
        .eq('id', fromRequestId).single();

      if (request) {
        const tripPricePerKg  = newTrip.price_per_kg  || parseFloat(tripMode === 'multi' ? multiForm.price_per_kg  : form.price_per_kg)  || 0;
        const tripDocPrice    = newTrip.document_price || parseFloat(tripMode === 'multi' ? multiForm.document_price : form.document_price) || 0;
        const kgSubtotal  = tripPricePerKg * (request.kg_needed || 0);
        const docSubtotal = (request.needs_documents && request.document_count > 0)
          ? tripDocPrice * request.document_count
          : 0;
        const totalPrice  = kgSubtotal + docSubtotal;
        const platformFee = totalPrice * 0.10;
        const payMode     = tripMode === 'multi' ? multiForm.payment_mode : form.payment_mode;
        const isDocOnly   = (!request.kg_needed || request.kg_needed === 0) && request.needs_documents;

        const { data: newBooking, error: bookingInsertErr } = await supabase.from('bookings').insert({
          trip_id: newTrip.id, sender_id: request.sender_id,
          kg_booked: request.kg_needed || 0, total_price: totalPrice,
          platform_fee: platformFee, status: 'proposed', payment_status: 'unpaid',
          sender_payment_mode: payMode === 'cash' ? 'cash' : 'platform',
          description: request.description, shipment_request_id: fromRequestId,
          marked_full: markFullAfter,
          recipient_name: request.recipient_name || null,
          recipient_phone: request.recipient_phone || null,
          booking_type:   isDocOnly ? 'document' : 'kg',
          document_type:  request.needs_documents ? request.document_type  : null,
          document_count: request.needs_documents ? request.document_count : null,
        }).select('id').single();

        if (bookingInsertErr || !newBooking) {
          console.error('[PostTrip] Booking insert failed:', bookingInsertErr?.message, { totalPrice, platformFee, kgSubtotal, docSubtotal, isDocOnly });
          setError('Trip created but booking proposal failed: ' + (bookingInsertErr?.message || 'unknown error'));
          setLoading(false);
          return;
        }

        if (markFullAfter) {
          await supabase.from('trips').update({ status: 'full' }).eq('id', newTrip.id);
        }

        await supabase.functions.invoke('send-notification', {
          body: {
            notification_context: 'proposal_received',
            notify_user_id: request.sender_id,
            record: { id: newBooking?.id, trip_id: newTrip.id, sender_id: request.sender_id, kg_booked: request.kg_needed || 0, total_price: totalPrice, platform_fee: platformFee, booking_type: isDocOnly ? 'document' : 'kg', document_type: request.document_type, document_count: request.document_count },
          },
        });
      }
    }
    navigate(fromRequestId ? '/dashboard?tab=trips' : '/dashboard');
  }

  function handleDragStart(idx) {
    dragSrcIdx.current = idx;
  }

  function handleDragOver(e, idx) {
    e.preventDefault();
    setDragOverIdx(idx);
  }

  function handleDrop(idx) {
    const from = dragSrcIdx.current;
    if (from === null || from === idx) { setDragOverIdx(null); return; }
    setStops(s => {
      const arr = [...s];
      const [dragged] = arr.splice(from, 1);
      arr.splice(idx, 0, dragged);
      return arr.map((st, i) => ({ ...st, sequence: i + 1 }));
    });
    dragSrcIdx.current = null;
    setDragOverIdx(null);
  }

  function handleDragEnd() {
    dragSrcIdx.current = null;
    setDragOverIdx(null);
  }

  // ── Stop card component ───────────────────────────────────────
  return (
    <div className="page post-trip-page" style={{ overflowX: 'hidden' }}>
      {fromRequestId && (
        <div style={{ background: 'rgba(76,175,125,0.08)', border: '1px solid rgba(76,175,125,0.2)', borderRadius: '12px', padding: '14px 18px', marginBottom: '24px', fontSize: '14px', color: 'rgba(76,175,125,0.9)', display: 'flex', gap: '10px', alignItems: 'flex-start', maxWidth: '680px', margin: '0 auto 24px' }}>
          <span style={{ fontSize: '18px', flexShrink: 0 }}>📬</span>
          <div><strong>Creating a trip for a sender request.</strong> The form is pre-filled. Once you publish, a booking will be created and the sender notified.</div>
        </div>
      )}
      <div className="container">
        <div className="pt-header">
          <h1>{t('postTrip.title')}</h1>
          <p>{t('postTrip.subtitle')}</p>
        </div>
        {error && <div className="alert alert-error">{error}</div>}
      {searchParams.get('requires_documents') === '1' && (
        <div style={{ background: 'rgba(76,175,125,0.08)', border: '1px solid rgba(76,175,125,0.25)', borderRadius: '12px', padding: '14px 16px', marginBottom: '20px', fontSize: '13px' }}>
          <div style={{ fontWeight: '700', color: '#4CAF7D', marginBottom: '4px' }}>📄 Document acceptance pre-enabled</div>
          <div style={{ color: 'var(--olive)' }}>This trip is being created for a request that includes documents. Document acceptance has been enabled automatically — you must keep it on and set a price per item.</div>
        </div>
      )}

        {/* ── TRIP MODE SELECTOR ────────────────────────────────── */}
        <div className="pt-section">
          <h3 className="pt-section-title">{t('postTrip.tripTypeTitle')}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' }}>
            <button
              type="button"
              onClick={() => setTripMode('simple')}
              style={{
                padding: '16px', borderRadius: '14px', border: `2px solid ${tripMode === 'simple' ? 'var(--red)' : 'rgba(255,255,255,0.08)'}`,
                background: tripMode === 'simple' ? 'rgba(232,52,26,0.1)' : 'rgba(255,255,255,0.03)',
                cursor: 'pointer', textAlign: 'left',
              }}
            >
              <div style={{ fontSize: '22px', marginBottom: '6px' }}>🗺️</div>
              <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px', color: 'var(--sand)' }}>{t('postTrip.simpleTitle')}</div>
              <div style={{ fontSize: '12px', color: 'var(--olive)', lineHeight: '1.5' }}>{t('postTrip.simpleDesc')}</div>
            </button>
            <button
              type="button"
              onClick={() => setTripMode('multi')}
              style={{
                padding: '16px', borderRadius: '14px', border: `2px solid ${tripMode === 'multi' ? 'var(--red)' : 'rgba(255,255,255,0.08)'}`,
                background: tripMode === 'multi' ? 'rgba(232,52,26,0.1)' : 'rgba(255,255,255,0.03)',
                cursor: 'pointer', textAlign: 'left',
              }}
            >
              <div style={{ fontSize: '22px', marginBottom: '6px' }}>🛑</div>
              <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px', color: 'var(--sand)' }}>{t('postTrip.multiTitle')}</div>
              <div style={{ fontSize: '12px', color: 'var(--olive)', lineHeight: '1.5' }}>{t('postTrip.multiDesc')}</div>
            </button>
          </div>
        </div>

        {/* ── DIRECTION (both modes) ────────────────────────────── */}
        <div className="pt-section">
          <h3 className="pt-section-title">Trip Direction</h3>
          <div className="pt-direction-selector">
            <button type="button" className={'pt-dir-btn' + (isDeToTn ? ' active' : '')} onClick={() => switchDirection('de_to_tn')}>
              <div className="pt-dir-flags">
                <img src="https://flagcdn.com/24x18/de.png" alt="DE" style={{ borderRadius: '2px' }} />
                <span style={{ margin: '0 6px' }}>→</span>
                <img src="https://flagcdn.com/24x18/tn.png" alt="TN" style={{ borderRadius: '2px' }} />
              </div>
              <div className="pt-dir-label">Germany to Tunisia</div>
              <div className="pt-dir-desc">Travelling from Germany, carrying goods to Tunisia</div>
            </button>
            <button type="button" className={'pt-dir-btn' + (!isDeToTn ? ' active' : '')} onClick={() => switchDirection('tn_to_de')}>
              <div className="pt-dir-flags">
                <img src="https://flagcdn.com/24x18/tn.png" alt="TN" style={{ borderRadius: '2px' }} />
                <span style={{ margin: '0 6px' }}>→</span>
                <img src="https://flagcdn.com/24x18/de.png" alt="DE" style={{ borderRadius: '2px' }} />
              </div>
              <div className="pt-dir-label">Tunisia to Germany</div>
              <div className="pt-dir-desc">Travelling from Tunisia, carrying goods to Germany</div>
            </button>
          </div>
        </div>

        {/* ═══════════════════════════════════════════════════════
            SIMPLE TRIP FORM
        ═══════════════════════════════════════════════════════ */}
        {tripMode === 'simple' && (
          <form className="pt-form" onSubmit={handleSubmitSimple}>
            <div className="pt-section">
              <h3 className="pt-section-title">Route</h3>
              <div className="grid-2">
                <div className="form-group">
                  <label>Departing from ({originCountry})</label>
                  <AddressAutocomplete value={form.origin_city} onChange={v => update('origin_city', v)} placeholder={`e.g. ${originCities[0]}`} countryCode={isDeToTn ? 'de' : 'tn'} type="city" required />
                </div>
                <div className="form-group">
                  <label>Arriving in ({destCountry})</label>
                  <AddressAutocomplete value={form.destination_city} onChange={v => update('destination_city', v)} placeholder={`e.g. ${destCities[0]}`} countryCode={isDeToTn ? 'tn' : 'de'} type="city" required />
                </div>
                <div className="form-group">
                  <label>{t('postTrip.departureDateLabel')}</label>
                  <input type="date" value={form.departure_date} onChange={e => update('departure_date', e.target.value)} required min={new Date().toISOString().split('T')[0]} />
                </div>
                <div className="form-group">
                  <label>{t('postTrip.arrivalDateLabel')}</label>
                  <input type="date" value={form.arrival_date} onChange={e => update('arrival_date', e.target.value)} min={form.departure_date || new Date().toISOString().split('T')[0]} />
                </div>
              </div>
            </div>

            <SimpleCapacitySection form={form} update={update} />
            <SimplePaymentSection form={form} update={update} />
            <SimplePickupSection form={form} update={update} />
            <SimpleDropoffSection form={form} update={update} />

            <DocumentSection
              accepts={form.accepts_documents}
              price={form.document_price}
              onToggle={v => update('accepts_documents', v)}
              onPrice={v => update('document_price', v)}
            />

            <div className="pt-section">
              <h3 className="pt-section-title">Notes for senders</h3>
              <div className="form-group">
                <textarea placeholder="e.g. I pass through Sousse before Sfax. Contact me 3 days before departure..." rows={4} value={form.notes} onChange={e => update('notes', e.target.value)} />
              </div>
            </div>

            <LegalAndSubmit
              liabilityAgreed={liabilityAgreed}
              setLiabilityAgreed={setLiabilityAgreed}
              loading={loading}
              fromRequestId={fromRequestId}
              markFullAfter={markFullAfter}
              setMarkFullAfter={setMarkFullAfter}
              t={t}
            />
          </form>
        )}

        {/* ═══════════════════════════════════════════════════════
            MULTI-STOP FORM
        ═══════════════════════════════════════════════════════ */}
        {tripMode === 'multi' && (
          <form className="pt-form" onSubmit={handleSubmitMulti}>

            {/* STOPS BUILDER */}
            <div className="pt-section">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '8px' }}>
                <div>
                  <h3 className="pt-section-title" style={{ margin: 0 }}>{t('postTrip.itineraryTitle')}</h3>
                  <p style={{ fontSize: '13px', color: 'var(--olive)', marginTop: '4px' }}>{t('postTrip.itinerarySubtitle')}</p>
                </div>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button type="button" className="btn btn-ghost" style={{ fontSize: '12px', padding: '7px 14px' }} onClick={() => addStop('pickup')}>
                    {t('postTrip.addPickupStop')}
                  </button>
                  <button type="button" className="btn btn-ghost" style={{ fontSize: '12px', padding: '7px 14px' }} onClick={() => addStop('delivery')}>
                    {t('postTrip.addDeliveryStop')}
                  </button>
                  <button type="button" className="btn btn-ghost" style={{ fontSize: '12px', padding: '7px 14px' }} onClick={() => addStop('port')}>
                    {t('postTrip.addPort')}
                  </button>
                </div>
              </div>

              {/* Route preview */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', background: 'rgba(255,255,255,0.03)', borderRadius: '10px', padding: '10px 14px', marginBottom: '16px', fontSize: '13px' }}>
                {stops.map((s, i) => (
                  <span key={i} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <span style={{
                      background: s.stop_type === 'pickup' ? 'rgba(232,52,26,0.2)' : s.stop_type === 'delivery' ? 'rgba(76,175,125,0.2)' : 'rgba(100,150,255,0.2)',
                      borderRadius: '100px', padding: '2px 10px', fontSize: '12px', fontWeight: '600',
                    }}>
                      {s.city || `Stop ${i+1}`}
                    </span>
                    {i < stops.length - 1 && <span style={{ color: 'var(--olive)' }}>→</span>}
                  </span>
                ))}
              </div>

              {stops.map((stop, idx) => (
                <StopCard
                  key={stop.sequence || idx}
                  stop={stop}
                  idx={idx}
                  total={stops.length}
                  isDeToTn={isDeToTn}
                  updateStop={updateStop}
                  moveStop={moveStop}
                  removeStop={removeStop}
                  t={t}
                  isDragOver={dragOverIdx === idx}
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={e => handleDragOver(e, idx)}
                  onDrop={() => handleDrop(idx)}
                  onDragEnd={handleDragEnd}
                />
              ))}

              {/* Port selector */}
              <div className="form-group" style={{ marginTop: '16px' }}>
                <label>{t('postTrip.portSelectorLabel')}</label>
                <select value={multiForm.port_city} onChange={e => updateMulti('port_city', e.target.value)}>
                  {PORT_CITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <span style={{ fontSize: '11px', color: 'var(--olive)' }}>{t('postTrip.portSelectorHint')}</span>
              </div>
            </div>

            {/* CAPACITY & PRICING */}
            <div className="pt-section">
              <h3 className="pt-section-title">Capacity and Pricing</h3>
              <div className="grid-2">
                <div className="form-group">
                  <label>Total capacity (kg)</label>
                  <input type="number" placeholder="e.g. 200" min="1" step="1" value={multiForm.total_kg} onChange={e => updateMulti('total_kg', e.target.value)} required />
                  <span style={{ fontSize: '11px', color: 'var(--olive)' }}>{t('postTrip.totalKgHint')}</span>
                </div>
                <div className="form-group">
                  <label>Price per kg (€)</label>
                  <input type="number" placeholder="e.g. 4.50" min="1" step="0.10" value={multiForm.price_per_kg} onChange={e => updateMulti('price_per_kg', e.target.value)} required />
                  <span style={{ fontSize: '11px', color: 'var(--olive)' }}>Transportily takes 10% platform fee on top</span>
                </div>
                <div className="form-group">
                  <label>Min kg per booking</label>
                  <input type="number" placeholder="e.g. 3" min="0.5" step="0.5" value={multiForm.min_kg} onChange={e => updateMulti('min_kg', e.target.value)} required />
                </div>
              </div>
              <div className="pt-checkboxes" style={{ marginTop: '8px' }}>
                <label className="pt-check">
                  <input type="checkbox" checked={multiForm.accepts_fragile} onChange={e => updateMulti('accepts_fragile', e.target.checked)} />
                  <div className="pt-check-content"><span className="pt-check-title">🫧 Fragile items accepted</span></div>
                </label>
                <label className="pt-check" style={{ marginTop: '8px' }}>
                  <input type="checkbox" checked={multiForm.accepts_bulky} onChange={e => updateMulti('accepts_bulky', e.target.checked)} />
                  <div className="pt-check-content"><span className="pt-check-title">📦 Bulky items accepted</span></div>
                </label>
              </div>
            </div>

            {/* PAYMENT */}
            <div className="pt-section">
              <h3 className="pt-section-title">Payment preference</h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', overflow: 'hidden' }}>
                {PAYMENT_MODE_OPTIONS.map(opt => (
                  <label key={opt.value} onClick={() => updateMulti('payment_mode', opt.value)} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', background: multiForm.payment_mode === opt.value ? 'rgba(232,52,26,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${multiForm.payment_mode === opt.value ? 'var(--red)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '10px', cursor: 'pointer', boxSizing: 'border-box', width: '100%', overflow: 'hidden' }}>
                    <input type="radio" name="multi_payment_mode" value={opt.value} checked={multiForm.payment_mode === opt.value} onChange={() => updateMulti('payment_mode', opt.value)} style={{ marginTop: '2px', accentColor: 'var(--red)', flexShrink: 0, width: '16px', height: '16px' }} />
                    <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
                      <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '3px', wordBreak: 'break-word' }}>{opt.label}</div>
                      <div style={{ fontSize: '12px', color: 'rgba(245,237,216,0.5)', lineHeight: '1.5', wordBreak: 'break-word' }}>{opt.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* HOME DELIVERY */}
            <div className="pt-section">
              <h3 className="pt-section-title">{t('postTrip.homeDeliveryTitle')}</h3>
              <label className="pt-check">
                <input type="checkbox" checked={multiForm.offers_home_delivery} onChange={e => updateMulti('offers_home_delivery', e.target.checked)} />
                <div className="pt-check-content">
                  <span className="pt-check-title">{t('postTrip.homeDeliveryCheck')}</span>
                  <span className="pt-check-desc">You will deliver to recipient's address (not just a pickup point)</span>
                </div>
              </label>
              {multiForm.offers_home_delivery && (
                <div className="form-group" style={{ marginLeft: '32px', marginTop: '8px' }}>
                  <label>Home delivery fee (€)</label>
                  <input type="number" placeholder="e.g. 5.00" min="0" step="0.5" value={multiForm.home_delivery_fee} onChange={e => updateMulti('home_delivery_fee', e.target.value)} style={{ maxWidth: '120px' }} />
                </div>
              )}
            </div>

            <DocumentSection
              accepts={multiForm.accepts_documents}
              price={multiForm.document_price}
              onToggle={v => updateMulti('accepts_documents', v)}
              onPrice={v => updateMulti('document_price', v)}
            />

            {/* NOTES */}
            <div className="pt-section">
              <h3 className="pt-section-title">Notes for senders</h3>
              <div className="form-group">
                <textarea placeholder="e.g. I'll be in Hannover for a few hours only. Call me before arrival. Goods must be packed and labelled." rows={4} value={multiForm.notes} onChange={e => updateMulti('notes', e.target.value)} />
              </div>
            </div>

            <LegalAndSubmit
              liabilityAgreed={liabilityAgreed}
              setLiabilityAgreed={setLiabilityAgreed}
              loading={loading}
              fromRequestId={fromRequestId}
              markFullAfter={markFullAfter}
              setMarkFullAfter={setMarkFullAfter}
              multiStop
              t={t}
            />
          </form>
        )}
      </div>
    </div>
  );
}


// ── Document pricing section ──────────────────────────────
function DocumentSection({ accepts, price, onToggle, onPrice }) {
  return (
    <div className="pt-section">
      <h3 className="pt-section-title">📄 Documents & small items</h3>
      <p style={{ fontSize: '13px', color: 'var(--olive)', marginBottom: '16px' }}>
        Accept documents, letters, and small parcels (under 500g) for a flat fee — independent of kg capacity.
      </p>
      <label className="pt-check">
        <input type="checkbox" checked={accepts} onChange={e => onToggle(e.target.checked)} />
        <div className="pt-check-content">
          <span className="pt-check-title">📨 I accept documents & envelopes</span>
          <span className="pt-check-desc">Official docs, diplomas, contracts, letters, small parcels under 500g</span>
        </div>
      </label>
      {accepts && (
        <div className="form-group" style={{ marginLeft: '32px', marginTop: '12px' }}>
          <label>Your flat fee per item / envelope (€)</label>
          <input
            type="number"
            placeholder="e.g. 5.00"
            min="1"
            step="0.50"
            value={price}
            onChange={e => onPrice(e.target.value)}
            style={{ maxWidth: '140px' }}
          />
          <span style={{ fontSize: '11px', color: 'var(--olive)' }}>
            Sender pays this per document or envelope. Platform takes 10% on top.
          </span>
          <div style={{ marginTop: '10px', background: 'rgba(76,175,125,0.08)', border: '1px solid rgba(76,175,125,0.2)', borderRadius: '10px', padding: '12px 14px', fontSize: '12px', color: 'rgba(245,237,216,0.6)', lineHeight: '1.7' }}>
            <div style={{ fontWeight: '700', color: '#4CAF7D', marginBottom: '6px' }}>ℹ️ How document bookings work</div>
            <div>• Document bookings are priced per item — not per kg</div>
            <div>• They do <strong style={{ color: 'var(--sand)' }}>not</strong> consume your kg capacity</div>
            <div>• Senders can book documents independently of your kg availability</div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Shared sub-components for simple trip ─────────────────────
function SimpleCapacitySection({ form, update }) {
  return (
    <div className="pt-section">
      <h3 className="pt-section-title">Capacity and Pricing</h3>
      <div className="grid-2">
        <div className="form-group">
          <label>Total capacity (kg)</label>
          <input type="number" placeholder="e.g. 30" min="1" step="0.5" value={form.total_kg} onChange={e => update('total_kg', e.target.value)} required />
        </div>
        <div className="form-group">
          <label>Price per kg (€)</label>
          <input type="number" placeholder="e.g. 4.50" min="1" step="0.10" value={form.price_per_kg} onChange={e => update('price_per_kg', e.target.value)} required />
          <span style={{ fontSize: '11px', color: 'var(--olive)' }}>Transportily takes 10% platform fee on top</span>
        </div>
        <div className="form-group">
          <label>Min kg per booking</label>
          <input type="number" placeholder="e.g. 3" min="0.5" step="0.5" value={form.min_kg} onChange={e => update('min_kg', e.target.value)} required />
        </div>
      </div>
    </div>
  );
}

function SimplePaymentSection({ form, update }) {
  return (
    <div className="pt-section">
      <h3 className="pt-section-title">Payment preference</h3>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', width: '100%', overflow: 'hidden' }}>
        {PAYMENT_MODE_OPTIONS.map(opt => (
          <label key={opt.value} onClick={() => update('payment_mode', opt.value)} style={{ display: 'flex', alignItems: 'flex-start', gap: '10px', padding: '12px 14px', background: form.payment_mode === opt.value ? 'rgba(232,52,26,0.1)' : 'rgba(255,255,255,0.03)', border: `1px solid ${form.payment_mode === opt.value ? 'var(--red)' : 'rgba(255,255,255,0.08)'}`, borderRadius: '10px', cursor: 'pointer', boxSizing: 'border-box', width: '100%', overflow: 'hidden' }}>
            <input type="radio" name="payment_mode" value={opt.value} checked={form.payment_mode === opt.value} onChange={() => update('payment_mode', opt.value)} style={{ marginTop: '2px', accentColor: 'var(--red)', flexShrink: 0, width: '16px', height: '16px' }} />
            <div style={{ minWidth: 0, flex: 1, overflow: 'hidden' }}>
              <div style={{ fontWeight: '600', fontSize: '13px', marginBottom: '3px', wordBreak: 'break-word' }}>{opt.label}</div>
              <div style={{ fontSize: '12px', color: 'rgba(245,237,216,0.5)', lineHeight: '1.5', wordBreak: 'break-word' }}>{opt.desc}</div>
            </div>
          </label>
        ))}
      </div>
    </div>
  );
}

function SimplePickupSection({ form, update }) {
  return (
    <div className="pt-section">
      <h3 className="pt-section-title">Pickup from sender</h3>
      <div className="pt-checkboxes">
        <label className="pt-check">
          <input type="checkbox" checked={form.home_pickup} onChange={e => update('home_pickup', e.target.checked)} />
          <div className="pt-check-content">
            <span className="pt-check-title">🏠 I can collect from the sender's address</span>
            <span className="pt-check-desc">I will go to the sender to pick up their goods</span>
          </div>
        </label>
        {form.home_pickup && (
          <div className="form-group" style={{ marginLeft: '32px', marginTop: '8px' }}>
            <label>Collection radius (km)</label>
            <input type="number" value={form.pickup_radius_km} onChange={e => update('pickup_radius_km', e.target.value)} min="1" max="100" style={{ maxWidth: '120px' }} />
          </div>
        )}
        <label className="pt-check" style={{ marginTop: '12px' }}>
          <input type="checkbox" checked={form.accepts_fragile} onChange={e => update('accepts_fragile', e.target.checked)} />
          <div className="pt-check-content"><span className="pt-check-title">🫧 Fragile items accepted</span></div>
        </label>
        <label className="pt-check" style={{ marginTop: '12px' }}>
          <input type="checkbox" checked={form.accepts_bulky} onChange={e => update('accepts_bulky', e.target.checked)} />
          <div className="pt-check-content"><span className="pt-check-title">📦 Bulky items accepted</span></div>
        </label>
      </div>
    </div>
  );
}

function SimpleDropoffSection({ form, update }) {
  return (
    <div className="pt-section">
      <h3 className="pt-section-title">Drop-off to receiver</h3>
      <div className="pt-checkboxes">
        <label className="pt-check">
          <input type="checkbox" checked={form.offers_pickup_location} onChange={e => update('offers_pickup_location', e.target.checked)} />
          <div className="pt-check-content">
            <span className="pt-check-title">📍 Fixed pickup location</span>
            <span className="pt-check-desc">Receiver comes to collect at a set address</span>
          </div>
        </label>
        {form.offers_pickup_location && (
          <div className="form-group" style={{ marginLeft: '32px', marginTop: '8px' }}>
            <label>Pickup address</label>
            <input type="text" placeholder="e.g. 12 Rue de la Liberté, Tunis" value={form.pickup_location_address} onChange={e => update('pickup_location_address', e.target.value)} style={{ maxWidth: '400px' }} />
          </div>
        )}
        <label className="pt-check" style={{ marginTop: '12px' }}>
          <input type="checkbox" checked={form.offers_home_delivery} onChange={e => update('offers_home_delivery', e.target.checked)} />
          <div className="pt-check-content">
            <span className="pt-check-title">🚚 Home delivery</span>
            <span className="pt-check-desc">I will deliver to the receiver's address</span>
          </div>
        </label>
        {form.offers_home_delivery && (
          <div className="form-group" style={{ marginLeft: '32px', marginTop: '8px' }}>
            <label>Delivery fee (€)</label>
            <input type="number" placeholder="e.g. 5.00" min="0" step="0.50" value={form.home_delivery_fee} onChange={e => update('home_delivery_fee', e.target.value)} style={{ maxWidth: '120px' }} />
          </div>
        )}
      </div>
    </div>
  );
}

function LegalAndSubmit({ liabilityAgreed, setLiabilityAgreed, loading, fromRequestId, markFullAfter, setMarkFullAfter, multiStop, t }) {
  return (
    <>
      <div className="pt-section">
        <h3 className="pt-section-title">Legal Agreement</h3>
        <LiabilityAgreement role="transporter" agreed={liabilityAgreed} onChange={setLiabilityAgreed} />
      </div>

      {fromRequestId && (
        <div onClick={() => setMarkFullAfter(v => !v)} style={{ marginBottom: '16px', background: markFullAfter ? 'rgba(232,197,71,0.1)' : 'rgba(232,197,71,0.04)', border: `1px solid ${markFullAfter ? 'rgba(232,197,71,0.4)' : 'rgba(232,197,71,0.15)'}`, borderRadius: '14px', padding: '16px 18px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '16px', userSelect: 'none' }}>
          <div style={{ width: '44px', height: '24px', borderRadius: '100px', flexShrink: 0, background: markFullAfter ? 'rgba(232,197,71,0.8)' : 'rgba(255,255,255,0.12)', position: 'relative' }}>
            <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'white', position: 'absolute', top: '3px', left: markFullAfter ? '23px' : '3px', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.3)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: '14px', fontWeight: '600', color: 'var(--sand)', marginBottom: '3px' }}>Mark trip as full after this booking</div>
            <div style={{ fontSize: '12px', color: 'var(--olive)', lineHeight: '1.5' }}>No other senders can book this trip.</div>
          </div>
        </div>
      )}

      <button type="submit" className="btn btn-primary" style={{ padding: '15px 40px', fontSize: '15px' }} disabled={loading || !liabilityAgreed}>
        {loading ? t('postTrip.publishingBtn') : fromRequestId ? 'Publish Trip & Book Sender' : multiStop ? t('postTrip.publishMultiBtn') : t('postTrip.publishBtn')}
      </button>
      {!liabilityAgreed && (
        <p style={{ fontSize: '12px', color: 'var(--olive)', marginTop: '10px' }}>You must agree to the liability terms before publishing.</p>
      )}
    </>
  );
}